import React, { useEffect } from "react";
import "../static/css/dashboard.css";
import { selectAuth } from "features/auth/AuthSlice";
import { useSelector } from "react-redux";
import { t, translate } from "components/CentralDataMangement/translation/Translation";
import { useNavigate } from "react-router-dom";
import { CANDIDATE_ROLE } from "Constants";

/**
 * HomePage Component
 * 
 * Welcome page displayed immediately after user login.
 * Shows a personalized greeting and automatically redirects users
 * to their role-specific dashboard after 3 seconds.
 * 
 * @component
 * @returns {JSX.Element} Welcome page with auto-redirect functionality
 * 
 * @example
 * // Component is typically rendered after successful authentication
 * <Route path="/home" element={<HomePage />} />
 */
const HomePage: React.FC = () => {
  // Get authenticated user details from Redux store
  const userAuth = useSelector(selectAuth);
  
  // Navigation hook for programmatic routing
  const navigate = useNavigate();

  /**
   * Auto-redirect effect
   * Redirects users to appropriate dashboard based on their role
   * Candidates go to todos overview, all others to main dashboard
   */
  useEffect(() => {
    // Set 3-second delay before redirect
    const timerId = setTimeout(() => {
      // Route candidates to their specific todo overview
      if (userAuth.role === CANDIDATE_ROLE) {
        navigate(`/todos/overview`);
      } else {
        // Route all other roles (admin, recruiter, etc.) to main dashboard
        navigate("/dashboard");
      }
    }, 3000); // 3 second delay for users to read welcome message

    // Cleanup function to clear timeout if component unmounts
    return () => {
      clearTimeout(timerId);
    };
  }, [navigate]); // Re-run only if navigate function changes

  return (
    <>
      {/* Welcome page container with full height centering */}
      <div className="row d-flex align-items-center justify-content-center welcome-page" style={{minHeight : "inherit"}}>
        {/* Content wrapper with flexbox centering */}
        <div className="col-md-12 mx-auto text-center h-100 d-flex align-items-center justify-content-center flex-column">
          
          {/* Welcome greeting with user's first name */}
          <div>
            {/* Translated welcome text */}
            <span className="welcome-text">{t("Welcome,")}</span> &nbsp;
            {/* User's first name in accent color */}
            <span className="name-text color-dark-pink">
              {userAuth.name.split(" ")[0]} {/* Extract first name from full name */}
            </span>
          </div>
          
          {/* Role-specific welcome message */}
          <div className="welcome-message-homepage position-relative mt-2">
            <div>
              {/* Dynamic message based on user role */}
              {`${t("Let's navigate the")} ${userAuth.role
                .replace("_", " ")  // Replace underscores with spaces (e.g., SUPER_ADMIN -> SUPER ADMIN)
                .toLowerCase()}`}   {/* Convert to lowercase for better readability */}
              <br></br>
              {/* Closing message */}
              {t("universe together") + "." }
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Export with translation HOC for internationalization support
export default translate(HomePage);
